import { getPasskey, passkeysSupported, type SSOOption, type StaffSSOButton } from '@central/api';
import { color } from '@central/ui';
import { Button, PasswordInput, TextInput } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';
import { useApi } from '../api';
import { FactorSteps } from '../components/FactorSteps';
import { ErrorText, LinkButton, Panel, SignInPage } from '../components/signInParts';
import { amberButton, inputStyles, outlineButton, quietButton } from '../components/signInStyles';
import { errorMessage } from '../lib/errors';
import { useSignedIn } from '../lib/session';
import { nextStep, type FactorStep } from '../lib/signInSteps';
import { leaveFor, ssoErrorMessage, ssoStartHref } from '../lib/sso';

const muted: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: '19px',
  color: color.signInMuted,
};

/**
 * Staff sign-in (docs/design/AdminSignIn.dc.html). Step 1 is a usernameless
 * passkey, email + password, or single sign-on: a "Continue with" button
 * per Rethink identity provider, or "Use my organization's SSO", which
 * finds a reseller's provider by the email's domain. Step 2 is a TOTP code,
 * a passkey or a recovery code; SSO skips it (the IdP owns the second
 * factor). Only a browser the user trusted skips step 2 of a password
 * sign-in. A user with no second factor enrolls one, then saves recovery
 * codes.
 */
export function SignIn() {
  const api = useApi();
  const signedIn = useSignedIn();
  const { ssoError } = useSearch({ from: '/sign-in' });
  const [step, setStep] = useState<FactorStep | { kind: 'password' }>({ kind: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [ssoNeedsEmail, setSsoNeedsEmail] = useState(false);
  // A list the server cannot give (an older server, a network error) shows no buttons.
  const providers = useQuery({
    queryKey: ['auth', 'sso', 'providers'],
    queryFn: () => api.auth.sso.providers(),
    retry: false,
    staleTime: Infinity,
  });
  const discover = useMutation({
    mutationFn: (address: string) => api.auth.sso.discover({ email: address }),
    onSuccess: (r, address) => {
      if (r.providers.length !== 1) return;
      const href = ssoStartHref(r.providers[0]!.startUrl, address);
      if (href) leaveFor(href);
    },
  });

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const passkey = () =>
    run(async () => {
      const { publicKey } = await api.auth.webauthnLoginBegin();
      const credential = await getPasskey(publicKey);
      await signedIn(await api.auth.webauthnLoginFinish({ credential }));
    });

  const submitPassword = () =>
    run(async () => {
      const res = await api.auth.login({ email: email.trim(), password });
      setPassword('');
      if (res.stage === 'full') await signedIn();
      else setStep(nextStep(res.methods));
    });

  const continueWith = (p: StaffSSOButton) => {
    const href = ssoStartHref(api.auth.sso.startUrl(p.id, email.trim() || undefined));
    if (href) leaveFor(href);
  };

  const findOrgSso = () => {
    const address = email.trim();
    setSsoNeedsEmail(!address);
    if (address) discover.mutate(address);
  };

  const startOver = () => {
    setError(undefined);
    setStep({ kind: 'password' });
  };

  const rethink = providers.data?.providers ?? [];
  const shownError = error ?? (discover.error ? errorMessage(discover.error) : undefined);

  return (
    <SignInPage>
      {step.kind === 'password' ? (
        <Panel step={1} of={2} title="Staff sign in" onSubmit={() => void submitPassword()}>
          {passkeysSupported() && (
            <>
              <Button
                type="button"
                fullWidth
                h={44}
                style={amberButton}
                onClick={() => void passkey()}
                disabled={busy}
              >
                Sign in with a passkey
              </Button>
              <OrRule />
            </>
          )}
          <TextInput
            label="Work email"
            type="email"
            autoComplete="username webauthn"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.currentTarget.value);
              setSsoNeedsEmail(false);
              discover.reset();
            }}
            error={
              ssoNeedsEmail ? 'Enter your work email to find your organization’s SSO.' : undefined
            }
            styles={inputStyles}
          />
          <PasswordInput
            label="Password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            styles={inputStyles}
          />
          <ErrorText>{shownError ?? (ssoError ? ssoErrorMessage(ssoError) : undefined)}</ErrorText>
          <Button type="submit" fullWidth h={44} loading={busy} style={quietButton}>
            Continue
          </Button>
          {rethink.length > 0 && (
            <>
              <OrRule />
              {rethink.map((p) => (
                <Button
                  key={p.id}
                  type="button"
                  variant="outline"
                  fullWidth
                  h={44}
                  style={outlineButton}
                  onClick={() => continueWith(p)}
                >
                  Continue with {p.name}
                </Button>
              ))}
            </>
          )}
          <LinkButton onClick={findOrgSso} disabled={discover.isPending}>
            Use my organization’s SSO
          </LinkButton>
          {discover.data && <SsoChoice providers={discover.data.providers} email={email.trim()} />}
        </Panel>
      ) : (
        <FactorSteps
          step={step}
          done={1}
          onStep={setStep}
          onStartOver={startOver}
          onSignedIn={signedIn}
        />
      )}
    </SignInPage>
  );
}

function OrRule() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: color.signInMuted,
        fontSize: 13,
      }}
    >
      <div style={{ flex: 1, height: 1, background: color.signInRule }} />
      or
      <div style={{ flex: 1, height: 1, background: color.signInRule }} />
    </div>
  );
}

/**
 * What discover found when it was not exactly one provider: none, or a
 * choice. A single provider is opened at once.
 */
function SsoChoice({ providers, email }: { providers: SSOOption[]; email: string }) {
  if (providers.length === 0) {
    return (
      <p style={muted}>
        Single sign-on is not set up for that email’s domain. Sign in with your password or a
        passkey.
      </p>
    );
  }
  if (providers.length === 1) {
    // A usable start has already been opened; a refused one is reported.
    return ssoStartHref(providers[0]!.startUrl, email) ? null : (
      <ErrorText>{ssoErrorMessage('sso_failed')}</ErrorText>
    );
  }
  return (
    <div
      role="group"
      aria-label="Choose your sign-on"
      style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {providers.map((p) => {
        const href = ssoStartHref(p.startUrl, email);
        return (
          <Button
            key={p.id}
            type="button"
            variant="outline"
            fullWidth
            h={44}
            style={outlineButton}
            disabled={!href}
            onClick={() => href && leaveFor(href)}
          >
            Continue with {p.name}
          </Button>
        );
      })}
    </div>
  );
}
